import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { api, buildWsUrl, requestWsTicket, wsBackoffMs } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { trace } from "@/lib/trace";
import { applyBranding } from "@/lib/branding";

const WSCtx = createContext(null);

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [currentId, setCurrentId] = useState(() => localStorage.getItem("vs_workspace") || null);
  // True once we know the visitor has several workspaces and has chosen none.
  const [needsChoice, setNeedsChoice] = useState(false);
  const [tenant, setTenant] = useState(null);   // workspace implied by the hostname
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState({ channels: {}, dms: {} });
  const listenersRef = useRef(new Set());

  const current = workspaces.find((w) => w.id === currentId) || null;

  const refreshWorkspaces = useCallback(async () => {
    const r = await api.get("/workspaces");
    setWorkspaces(r.data);
    return r.data;
  }, []);

  useEffect(() => {
    if (!user) { setWorkspaces([]); setLoading(false); return; }
    refreshWorkspaces().finally(() => setLoading(false));
  }, [user, refreshWorkspaces]);

  // A subdomain names its workspace, so there is nothing to choose there.
  useEffect(() => {
    api.get("/tenant")
      .then((r) => setTenant(r.data.workspace || null))
      .catch(() => setTenant(null));
  }, []);

  useEffect(() => {
    if (workspaces.length === 0) return;
    if (currentId && workspaces.find((w) => w.id === currentId)) { setNeedsChoice(false); return; }
    trace("workspace:resolving", { currentId, count: workspaces.length, tenant: tenant?.id || null });
    const fromHost = tenant && workspaces.find((w) => w.id === tenant.id);
    if (fromHost) {
      trace("workspace:switch", { from: currentId, to: fromHost.id, why: "tenant" });
      setCurrentId(fromHost.id);
      localStorage.setItem("vs_workspace", fromHost.id);
      setNeedsChoice(false);
      return;
    }
    if (workspaces.length > 1) { setNeedsChoice(true); return; }
    const next = workspaces[0].id;
    trace("workspace:switch", { from: currentId, to: next, why: "only one" });
    setCurrentId(next);
    localStorage.setItem("vs_workspace", next);
    setNeedsChoice(false);
  }, [workspaces, currentId, tenant]);

  // Branding follows the active workspace, including back to the default when it
  // has none. `current` is a fresh object after every workspace_updated event, so
  // an owner's edit lands for everyone without a reload.
  useEffect(() => { applyBranding(current); }, [current]);

  const switchWorkspace = useCallback((wid) => {
    setCurrentId(wid);
    localStorage.setItem("vs_workspace", wid);
    setNeedsChoice(false);
  }, []);

  const createWorkspace = useCallback(async (name) => {
    const r = await api.post("/workspaces", { name });
    await refreshWorkspaces();
    switchWorkspace(r.data.id);
    return r.data;
  }, [refreshWorkspaces, switchWorkspace]);

  const joinWorkspace = useCallback(async (code) => {
    const r = await api.post("/workspaces/join", { code });
    await refreshWorkspaces();
    switchWorkspace(r.data.id);
    return r.data;
  }, [refreshWorkspaces, switchWorkspace]);

  // WebSocket - shared across the app
  const wsRef = useRef(null);
  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  // The socket, and getting it back.
  //
  // Each connection starts with a ticket the server mints — one handshake,
  // thirty seconds — so the session token never reaches the URL. There is no
  // fallback to it: a fallback is the defect waiting for the first slow minute.
  //
  // Reconnection is new. The effect used to depend on [user] alone, so a socket
  // that dropped stayed dropped until the page was reloaded: no live messages,
  // no incoming calls, and nothing on screen to say so.
  useEffect(() => {
    if (!user) return undefined;
    let live = true;
    let attempt = 0;
    let timer = null;
    let socket = null;

    const connect = async () => {
      if (!live) return;
      let ticket;
      try {
        ticket = await requestWsTicket(localStorage.getItem("vs_workspace") || null);
      } catch {
        // No ticket, no socket. Try again later rather than reach for the
        // session token — the whole point of this is that there is no such path.
        return retry();
      }
      if (!live) return;
      try {
        socket = new WebSocket(buildWsUrl(ticket));
      } catch {
        return retry();
      }
      wsRef.current = socket;
      socket.onopen = () => { attempt = 0; };
      socket.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          listenersRef.current.forEach((fn) => fn(m));
        } catch (_e) { /* ignore */ }
      };
      socket.onclose = () => { if (live) retry(); };
    };

    const retry = () => {
      if (!live) return;
      const wait = wsBackoffMs(attempt);
      attempt += 1;
      timer = setTimeout(connect, wait);
    };

    connect();
    return () => {
      live = false;
      clearTimeout(timer);
      try { socket?.close(); } catch (_e) { /* ignore */ }
    };
  }, [user]);

  // Unread refresh
  const refreshUnread = useCallback(async () => {
    if (!currentId) return;
    try {
      const r = await api.get(`/workspaces/${currentId}/unread`);
      setUnread(r.data);
    } catch (_e) { /* ignore */ }
  }, [currentId]);

  useEffect(() => { refreshUnread(); }, [refreshUnread]);

  // Refresh unread when new messages arrive
  useEffect(() => {
    return subscribe((ev) => {
      if (ev.type === "message" || ev.type === "message_update") {
        refreshUnread();
      } else if (ev.type === "workspace_added" || ev.type === "member_joined") {
        refreshWorkspaces();
      } else if (ev.type === "workspace_deleted" || ev.type === "workspace_removed") {
        const wid = ev.data?.workspace_id;
        if (wid === currentId) {
          // current workspace gone - clear selection
          localStorage.removeItem("vs_workspace");
          setCurrentId(null);
        }
        refreshWorkspaces();
      } else if (ev.type === "workspace_updated") {
        refreshWorkspaces();
      } else if (ev.type === "member_removed" || ev.type === "member_role_changed") {
        // members panel may want to refresh; no global state change here
      }
    });
  }, [subscribe, refreshUnread, refreshWorkspaces, currentId]);

  const markRead = useCallback(async ({ channel_id, dm_partner_id }) => {
    if (!currentId) return;
    try {
      await api.post("/read", { workspace_id: currentId, channel_id, dm_partner_id });
      setUnread((prev) => {
        const next = { channels: { ...prev.channels }, dms: { ...prev.dms } };
        if (channel_id) delete next.channels[channel_id];
        if (dm_partner_id) delete next.dms[dm_partner_id];
        return next;
      });
    } catch (_e) { /* ignore */ }
  }, [currentId]);

  return (
    <WSCtx.Provider value={{
      workspaces, current, currentId, loading,
      switchWorkspace, createWorkspace, joinWorkspace, refreshWorkspaces,
      needsChoice, tenant,
      subscribe, unread, markRead, refreshUnread,
    }}>
      {children}
    </WSCtx.Provider>
  );
}

export const useWorkspace = () => useContext(WSCtx);
